import View from '../core/view/View';
import { node } from '../core/node/Node';

/**
 * If an object implements this interface, the `resolve` helper view will be able to
 * create a suitable `View` instance for it.
 *
 * #### Basic usage:
 *
```typescript
  register('SomeView', class extends View<SomeViewModel> { ... });

  class SomeViewModel implements Resolvable {
      public getViewName(): string {
          return 'SomeView';
      }
  }
```
 *
 * #### See also:
 *
 *  * The `register` function exported by the `.../view/View` module,
 *    which is used to establish a relationship between a registration name
 *    (as returned by `getViewName`) and a `View` class.
 */
export interface Resolvable {
	/**
	 * Returns a registration name of the `View` class that should be used for this object.
	 */
	getViewName(): string;
}

function isResolvable(data: any): data is Resolvable {
	return data && typeof data.getViewName !== 'undefined';
}

/**
 * A helper view that creates a suitable `View` instance for the given `data`.
 * `data` must implement a `getViewName(): string` method that returns the registration name
 * of the `View` class to use.
 *
 * #### Basic usage:
 *
```html
  <!-- Use the helper view to instantiate a view for `resolvable`: -->
  <div view="resolve" data="[[resolvable]]"></div>
```
 *
 * Don't forget to register the helper view before use, e.g. by importing this module:
 *
```typescript
  import Resolve from '.../components/Resolve';
  register('resolve', Resolve);
```
 *
 * #### See also:
 *
 *  * The `Resolvable` interface exported by this module, which defines the contract
 *    (i.e. the `getViewName(): string` method) that `data` objects must implement
 *    in order to work together with this helper view.
 */
export class Resolve extends View {

	public constructor() {
		super({ template: '<div></div>' });
	}

	public get data(): Resolvable {
		return this._data;
	}

	public set data(value: Resolvable) {
		if (!value) return;
		const previousValue = this._data;
		this._data = value;
		if (value !== previousValue) {
			this.update();
		}
	}

	protected init() {
		this.update();
	}

	public update() {
		if (!this.node) return;
		const viewName = isResolvable(this._data) ? this._data.getViewName() : 'default';
		const view: View = this.createChild( viewName );
		if (view) {
			view.data = this._data;
			view.render();
			node( this.node ).empty();
			this.node.append(view.node);
		}
	}
}


export default Resolve;
